import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, TrendingUp, Clock, Users, Zap, ChevronRight, Instagram, MessageCircle, Mail, Crown, Timer, Star, ChevronLeft, Cloud, Award, Play, Volume2, Maximize, Database, Gamepad2, Server, Activity, Film, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const LandingPage = () => {
  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  // Pause the carousel auto-advance while the user is hovering the slide
  // area — clearly UX-respectful: lets them read at their own pace.
  const [carouselPaused, setCarouselPaused] = useState(false);
  // Lightbox state — opens when a "novedades" card is clicked. Holds the
  // currently-viewed shot ({src,label,alt}) or null when closed. Close via
  // ESC or background click. Pure UI state, not persisted.
  const [lightboxShot, setLightboxShot] = useState(null);

  // Close lightbox on ESC key.
  useEffect(() => {
    if (!lightboxShot) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxShot(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxShot]);
  
  // Background images for carousel slides
  const slideBackgrounds = [
    '/racing-bg-1.jpg',
    '/racing-bg-2.jpg',
    '/racing-bg-3.jpg',
    '/racing-bg-4.jpg',
    '/racing-bg-1.jpg',
    '/racing-bg-2.jpg',
  ];

  // Preload all carousel images on mount
  useEffect(() => {
    const uniqueImages = [...new Set(slideBackgrounds)];
    let loadedCount = 0;
    
    uniqueImages.forEach((src) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === uniqueImages.length) {
          setImagesLoaded(true);
        }
      };
      img.src = src;
    });
  }, []);
  
  const carouselSlides = [
    {
      id: 'novedades',
      type: 'novedades',
      title: "Mejoras en",
      titleHighlight: "Análisis",
      description: "Las features de análisis que más nos pidieron, ahora en vivo: cómo ganás o perdés tiempo en cada parcial, tu evolución a lo largo del año y un Box Timer pensado para ingenieros de pista.",
      shots: [
        { src: "/feature-realvsoptimum-1080.jpg", label: "REAL vs ÓPTIMA",        alt: "Comparativa pedagógica: vuelta rápida real vs vuelta óptima teórica" },
        { src: "/feature-heatmap-1080.jpg",      label: "Sector Heatmap",       alt: "Mapa de calor de sectores: dónde gana y pierde tiempo cada piloto" },
        { src: "/feature-evolution-1080.jpg",    label: "Evolución del piloto", alt: "Progresión de la vuelta rápida fecha por fecha" },
        { src: "/feature-timer-1080.jpg",        label: "Box Timer del ingeniero", alt: "Cronómetros simultáneos persistentes en la nube" },
      ],
    },
    {
      id: 'replay',
      type: 'replay',
      title: "Replay",
      titleHighlight: "Studio",
      description: "Sincronizá tu video onboard con la telemetría real: velocidad, RPM, temperaturas, G laterales/longitudinales y mini-mapa, todo sobre la imagen al milisegundo.",
      formats: {
        video: ["mp4", "mov", "m4v"],
        data: [".csv", ".vbo (RaceLogic)", "AIM Race Studio"],
      },
      // Screenshots hosted via the public artifacts CDN.
      shots: [
        "https://customer-assets.emergentagent.com/job_e27f15ce-23e5-4ab6-9462-923fd23a705b/artifacts/w0ou8u8l_image.png",
        "https://customer-assets.emergentagent.com/job_e27f15ce-23e5-4ab6-9462-923fd23a705b/artifacts/he31ap63_image.png",
        "https://customer-assets.emergentagent.com/job_e27f15ce-23e5-4ab6-9462-923fd23a705b/artifacts/wn9g2wrw_image.png",
      ],
    },
    {
      id: 'hero',
      type: 'hero',
      title: "Análisis de",
      titleHighlight: "Tiempos",
      subtitle: "para",
      subtitleHighlight: "Carreras",
      description: "Herramienta profesional para ingenieros de pista. Compara pilotos, analiza sectores y optimiza el rendimiento.",
    },
    {
      id: 'pro',
      type: 'feature',
      title: "Análisis PRO",
      titleHighlight: "Ilimitado",
      description: "Sube sesiones ilimitadas, compara múltiples pilotos simultáneamente y accede a todas las herramientas de análisis avanzado.",
      features: ["Sesiones ilimitadas", "Comparación multi-piloto", "Pilotos favoritos", "Historial completo"],
      icon: Crown,
    },
    {
      id: 'timer',
      type: 'timer',
      title: "Temporizador de",
      titleHighlight: "Box",
      description: "Herramienta esencial para equipos y control de pilotos. Gestiona el tiempo entre tandas con precisión profesional.",
      timerFeatures: [
        { icon: Play, title: "Countdown en Vivo", desc: "Cuenta regresiva precisa con visualización clara" },
        { icon: Volume2, title: "Alertas Sonoras", desc: "Notificaciones audibles al finalizar" },
        { icon: Maximize, title: "Pantalla Completa", desc: "Modo fullscreen para mejor visibilidad en boxes" },
        { icon: Database, title: "Persistencia", desc: "Los timers se guardan y sobreviven recargas" },
      ],
    },
    {
      id: 'drive',
      type: 'feature',
      title: "Google Drive",
      titleHighlight: "Sync",
      description: "Sincronización automática con Google Drive. Los archivos nuevos se detectan y notifican automáticamente.",
      features: ["Sync automático", "Notificaciones push", "Importación directa", "Organización por fecha"],
      icon: Cloud,
    },
    {
      id: 'ranking',
      type: 'feature',
      title: "Ranking de",
      titleHighlight: "Parciales",
      description: "Visualiza el ranking completo de cada sector. Identifica fortalezas y debilidades de cada piloto.",
      features: ["Ranking por sector", "Gap al líder", "Vuelta teórica perfecta", "Favoritos resaltados"],
      icon: Award,
    },
    {
      id: 'simracing',
      type: 'simracing',
      title: "Entrenamientos",
      titleHighlight: "Simracing",
      description: "Accede a nuestro servidor exclusivo de Assetto Corsa. Entrena en circuitos argentinos con configuraciones reales.",
      features: [
        { icon: Server, title: "Servidor Dedicado", desc: "24/7 con circuitos argentinos" },
        { icon: Activity, title: "Telemetría en Vivo", desc: "Análisis de datos en tiempo real" },
        { icon: Users, title: "Comunidad", desc: "Entrena con otros pilotos" },
        { icon: BarChart3, title: "Analytics", desc: "Todo lo de PRO incluido" },
      ],
    },
  ];

  // Auto-advance carousel.
  //   - Previous value (3 s) was too fast: users couldn't finish reading the
  //     description (~30 words) before the slide flipped. Bumped to 7 s
  //     which matches the read-time of the longest copy at average pace.
  //   - Pauses while lightbox is open (so the slide doesn't change behind
  //     the zoomed image) and while the user is hovering the slide area.
  useEffect(() => {
    if (!imagesLoaded) return;
    if (lightboxShot) return;
    if (carouselPaused) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [imagesLoaded, lightboxShot, carouselPaused, carouselSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length);
  };

  // Fixed height for all slides to maintain consistency
  const slideHeight = "min-h-[420px]";

  // Render different slide types
  const renderSlide = (slide) => {
    if (slide.type === 'novedades') {
      // Highlights strip — 4 square cards showcasing recent product updates.
      // Each card links visually to a discrete feature but they share the
      // same visual rhythm of the existing replay strip so the carousel
      // doesn't feel jarring as it auto-advances.
      return (
        <div
          className={`${slideHeight} flex flex-col items-center justify-center px-4`}
          data-testid="landing-slide-novedades"
        >
          <div className="text-center mb-5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white shadow-lg shadow-red-900/40">
              <Sparkles className="h-3 w-3" />
              Nuevo en {slide.titleHighlight}
            </span>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-tight">
              {slide.title} <span className="text-red-500">{slide.titleHighlight}</span>
            </h2>
            <p className="mt-3 text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {slide.description}
            </p>
          </div>

          {/* 4-up grid of square highlight cards. Each is a real <button>
              so it's keyboard-accessible and clearly opens a lightbox — no
              "phantom" hover state. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-5xl mx-auto w-full mb-2">
            {slide.shots.map((s, idx) => (
              <button
                key={s.src}
                type="button"
                onClick={() => setLightboxShot(s)}
                className="aspect-square overflow-hidden rounded-lg border border-zinc-800 bg-black transition hover:border-red-600/60 group relative cursor-pointer focus:outline-none focus:border-red-500"
                data-testid={`landing-novedades-shot-${idx + 1}`}
                aria-label={`Ver ${s.label} ampliada`}
              >
                <img
                  src={s.src}
                  alt={s.alt || s.label}
                  loading="lazy"
                  className="h-full w-full object-cover transition group-hover:scale-105 group-hover:opacity-95 pointer-events-none"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-2 pointer-events-none">
                  <p className="text-[10px] sm:text-xs font-semibold text-white uppercase tracking-wider">
                    {s.label}
                  </p>
                </div>
                {/* Subtle "click to zoom" indicator on hover */}
                <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition rounded-full bg-black/70 backdrop-blur p-1 pointer-events-none">
                  <Maximize className="h-3 w-3 text-white" />
                </span>
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (slide.type === 'replay') {
      // Premier slide: introduces Replay Studio as the headline product.
      // Designed to fit the same `slideHeight` envelope as the others so
      // the carousel rhythm stays consistent.
      return (
        <div
          className={`${slideHeight} flex flex-col items-center justify-center px-4`}
          data-testid="landing-slide-replay"
        >
          <div className="text-center mb-5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white shadow-lg shadow-red-900/40">
              <Sparkles className="h-3 w-3" />
              Nuevo
            </span>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-tight">
              {slide.title} <span className="text-red-500">{slide.titleHighlight}</span>
            </h2>
            <p className="mt-3 text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {slide.description}
            </p>
          </div>

          {/* 3 product screenshots — same strip layout as the in-app banner.
              Each opens the lightbox so visitors can inspect detail before
              signing up. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl mx-auto w-full mb-5">
            {slide.shots.map((src, idx) => (
              <button
                key={src}
                type="button"
                onClick={() => setLightboxShot({
                  src,
                  label: `Replay Studio · screenshot ${idx + 1} de ${slide.shots.length}`,
                  alt: `Replay Studio screenshot ${idx + 1}`,
                })}
                className="aspect-video overflow-hidden rounded-lg border border-zinc-800 bg-black transition hover:border-red-600/60 group relative cursor-pointer focus:outline-none focus:border-red-500"
                data-testid={`landing-replay-shot-${idx + 1}`}
                aria-label={`Ver screenshot ${idx + 1} ampliada`}
              >
                <img src={src} alt="" loading="lazy" className="h-full w-full object-cover transition group-hover:scale-105 pointer-events-none" />
                <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition rounded-full bg-black/70 backdrop-blur p-1 pointer-events-none">
                  <Maximize className="h-3 w-3 text-white" />
                </span>
              </button>
            ))}
          </div>

          {/* Format pills */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-center gap-3 sm:gap-6 text-xs mb-5 max-w-3xl mx-auto">
            <FormatRow icon={<Film className="h-3 w-3" />} label="Video" items={slide.formats.video} />
            <FormatRow label="Telemetría" items={slide.formats.data} />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/login">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                Probalo gratis
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-red-600 text-red-500 hover:bg-red-950/30 px-8 py-6 text-lg">
                <Crown className="w-5 h-5 mr-2 text-yellow-500" />
                Plan PRO
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    if (slide.type === 'hero') {
      return (
        <div className={`${slideHeight} flex flex-col items-center justify-center text-center px-4`}>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            {slide.title} <span className="text-red-500">{slide.titleHighlight}</span> {slide.subtitle}
            <span className="text-red-500"> {slide.subtitleHighlight}</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            {slide.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                Comenzar Gratis
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-red-600 text-red-500 hover:bg-red-950/30 px-8 py-6 text-lg">
                <Crown className="w-5 h-5 mr-2 text-yellow-500" />
                Ver Plan PRO
              </Button>
            </Link>
          </div>
        </div>
      );
    }
    
    if (slide.type === 'timer') {
      return (
        <div className={`${slideHeight} flex flex-col items-center justify-center px-4`}>
          <div className="text-center mb-6">
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              {slide.title} <span className="text-red-500">{slide.titleHighlight}</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              {slide.description}
            </p>
          </div>
          
          {/* Timer Preview */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto w-full">
            {/* Timer Visual Mock */}
            <div className="bg-neutral-900 border border-red-900/50 rounded-xl p-5">
              <div className="text-center">
                <p className="text-gray-400 text-xs mb-1">PILOTO EN PISTA</p>
                <p className="text-white font-bold mb-3">#42 - RODRIGUEZ, MARTIN</p>
                
                {/* Timer Display Mock */}
                <div className="bg-black rounded-lg p-4 border-2 border-red-600">
                  <p className="text-5xl font-mono font-bold text-red-500">04:32</p>
                  <p className="text-gray-500 text-xs mt-1">TIEMPO RESTANTE</p>
                </div>
                
                <div className="flex justify-center gap-2 mt-3">
                  <div className="px-3 py-1 bg-red-600/20 border border-red-600 rounded text-red-500 text-xs">
                    CLASE 2
                  </div>
                  <div className="px-3 py-1 bg-neutral-800 border border-neutral-600 rounded text-gray-400 text-xs">
                    CAJÓN 15
                  </div>
                </div>
              </div>
            </div>
            
            {/* Features List */}
            <div className="space-y-3">
              {slide.timerFeatures.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-neutral-900/50 border border-neutral-800 rounded-lg p-3">
                  <div className="w-8 h-8 bg-red-600/20 border border-red-600/50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm">{feature.title}</h4>
                    <p className="text-gray-400 text-xs">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    
    // Simracing slide
    if (slide.type === 'simracing') {
      return (
        <div className={`${slideHeight} flex flex-col items-center justify-center px-4`}>
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-600/30 to-red-600/30 border-2 border-orange-500 rounded-2xl mb-4">
              <Gamepad2 className="w-8 h-8 text-orange-500" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              {slide.title} <span className="text-orange-500">{slide.titleHighlight}</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              {slide.description}
            </p>
          </div>
          
          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto w-full mb-6">
            {slide.features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-neutral-900/50 border border-orange-900/30 rounded-lg p-4">
                <div className="w-10 h-10 bg-orange-600/20 border border-orange-600/50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">{feature.title}</h4>
                  <p className="text-gray-400 text-sm">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/simracing">
              <Button size="lg" className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-6 text-lg">
                <Gamepad2 className="w-5 h-5 mr-2" />
                Acceder a Simracing
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-950/30 px-8 py-6 text-lg">
                Ver Precio
              </Button>
            </Link>
          </div>
        </div>
      );
    }
    
    // Default feature slide
    return (
      <div className={`${slideHeight} flex flex-col items-center justify-center px-4`}>
        <div className="flex flex-col md:flex-row items-center gap-8 max-w-4xl mx-auto">
          {/* Icon */}
          <div className="w-28 h-28 bg-red-600/20 border-2 border-red-600 rounded-2xl flex items-center justify-center flex-shrink-0">
            <slide.icon className="w-14 h-14 text-red-500" />
          </div>
          
          {/* Content */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {slide.title} <span className="text-red-500">{slide.titleHighlight}</span>
            </h2>
            <p className="text-lg text-gray-300 mb-6">{slide.description}</p>
            
            {/* Features */}
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              {slide.features.map((feature, idx) => (
                <span 
                  key={idx}
                  className="px-4 py-2 bg-neutral-900 border border-red-900/50 rounded-full text-sm text-gray-300"
                >
                  {feature}
                </span>
              ))}
            </div>
            
            <div className="mt-6">
              <Link to="/login">
                <Button className="bg-red-600 hover:bg-red-700">
                  Comenzar Ahora
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-white border-b-4 border-red-600 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4 hover:opacity-90 transition-opacity cursor-pointer">
              <img 
                src="/datapro-logo.png" 
                alt="DATAPRO" 
                className="h-20 md:h-28 w-auto object-contain"
              />
              <div className="hidden md:block border-l-2 border-red-600 pl-4">
                <p className="text-black font-bold text-xl">Ingeniería de Pista</p>
                <p className="text-gray-600 text-sm">Racing Analytics</p>
              </div>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/simracing">
                <Button variant="ghost" className="text-gray-700 hover:text-orange-600 flex items-center gap-1 border border-orange-500/50 hover:border-orange-500">
                  <Gamepad2 className="w-4 h-4 text-orange-500" />
                  <span className="hidden sm:inline">Simracing</span>
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="ghost" className="text-gray-700 hover:text-red-600 flex items-center gap-1">
                  <Crown className="w-4 h-4 text-yellow-500" />
                  Plan PRO
                </Button>
              </Link>
              <Link to="/login">
                <Button className="bg-red-600 hover:bg-red-700 text-white">
                  Iniciar Sesión
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Simracing Banner - Destacado */}
      <div className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 py-3">
        <div className="container mx-auto px-6">
          <Link to="/simracing" className="flex items-center justify-center gap-4 group">
            <div className="flex items-center gap-2">
              <Gamepad2 className="w-6 h-6 text-white animate-pulse" />
              <span className="text-white font-bold text-lg">NUEVO: Entrenamientos Simracing</span>
            </div>
            <span className="text-white/80 hidden sm:inline">Assetto Corsa con Live Timing</span>
            <Button size="sm" variant="secondary" className="bg-white text-red-600 hover:bg-gray-100 font-semibold group-hover:scale-105 transition-transform">
              Acceder
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero Carousel Section */}
      <section className="relative py-16 px-6 overflow-hidden min-h-[500px]">
        {/* Dynamic Background Image - Black & White with low opacity */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat transition-opacity duration-500"
          style={{ 
            backgroundImage: `url(${slideBackgrounds[currentSlide]})`,
            backgroundPosition: 'center 60%',
            filter: 'grayscale(100%)',
            opacity: imagesLoaded ? 0.15 : 0
          }}
        />
        {/* Gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-red-900/40"></div>
        <div className="container mx-auto relative z-10">
          
          {/* Carousel Container. Hovering pauses the auto-advance so the
              user can finish reading the copy without the slide flipping. */}
          <div
            className="relative"
            onMouseEnter={() => setCarouselPaused(true)}
            onMouseLeave={() => setCarouselPaused(false)}
            data-testid="landing-carousel"
          >
            {/* Carousel Content */}
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {carouselSlides.map((slide, index) => (
                  <div key={slide.id} className="w-full flex-shrink-0 px-4">
                    {renderSlide(slide)}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Arrows - Fixed position */}
            <button 
              onClick={(e) => { e.preventDefault(); prevSlide(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-neutral-900/80 hover:bg-red-600 border border-red-900/50 rounded-full flex items-center justify-center transition-colors z-20"
              style={{ transform: 'translateY(-50%)' }}
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button 
              onClick={(e) => { e.preventDefault(); nextSlide(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-neutral-900/80 hover:bg-red-600 border border-red-900/50 rounded-full flex items-center justify-center transition-colors z-20"
              style={{ transform: 'translateY(-50%)' }}
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </div>
          
          {/* Dots Indicator */}
          <div className="flex justify-center gap-3 mt-8">
            {carouselSlides.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-red-500 w-10' 
                    : 'bg-neutral-700 hover:bg-neutral-600 w-2'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-neutral-950">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Todo lo que necesitas para el <span className="text-red-500">análisis</span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="bg-neutral-900 border-red-900/30 hover:border-red-600 transition-all">
              <CardContent className="p-6">
                <BarChart3 className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Comparación de Pilotos</h3>
                <p className="text-gray-400">
                  Compara el rendimiento de múltiples pilotos con gráficos claros y deltas por sector.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-neutral-900 border-red-900/30 hover:border-red-600 transition-all">
              <CardContent className="p-6">
                <TrendingUp className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Análisis de Consistencia</h3>
                <p className="text-gray-400">
                  Mide la desviación estándar y coeficiente de variación para evaluar la regularidad.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-neutral-900 border-red-900/30 hover:border-red-600 transition-all">
              <CardContent className="p-6">
                <Clock className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Vuelta por Vuelta</h3>
                <p className="text-gray-400">
                  Analiza la evolución del piloto vuelta a vuelta con tiempos de sectores detallados.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-neutral-900 border-red-900/30 hover:border-red-600 transition-all">
              <CardContent className="p-6">
                <Users className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Multi-Sesión</h3>
                <p className="text-gray-400">
                  Compara el progreso de un piloto entre diferentes sesiones de entrenamiento.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-neutral-900 border-red-900/30 hover:border-red-600 transition-all">
              <CardContent className="p-6">
                <Timer className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Temporizador de Box</h3>
                <p className="text-gray-400">
                  Control de tiempo entre tandas para equipos. Alertas sonoras y pantalla completa.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-neutral-900 border-red-900/30 hover:border-red-600 transition-all">
              <CardContent className="p-6">
                <Zap className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Múltiples Formatos</h3>
                <p className="text-gray-400">
                  Compatible con Turismo Pista, CarShow Santafesino y más. XLS y PDF soportados.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-red-900 to-red-700">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">¿Listo para optimizar tu rendimiento?</h2>
          <p className="text-xl text-red-100 mb-8">
            Comienza gratis y actualiza a PRO cuando lo necesites.
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold">
              Crear Cuenta Gratis
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-950 border-t border-red-900/30 py-12 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <img 
                src="/datapro-logo-white.png" 
                alt="DATAPRO" 
                className="h-20 w-auto object-contain mb-4"
              />
              <p className="text-gray-400 text-sm">
                Ingeniería de Pista - Racing Analytics<br />
                Argentina
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contacto</h4>
              <div className="space-y-3">
                <a 
                  href="https://www.instagram.com/datapro_analisis"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                  @datapro_analisis
                </a>
                <a 
                  href="https://wa.me/543492394950"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-400 hover:text-green-500 transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp
                </a>
                <a 
                  href="mailto:dataproargentina@gmail.com"
                  className="flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  dataproargentina@gmail.com
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Links</h4>
              <div className="space-y-2">
                <Link to="/pricing" className="block text-gray-400 hover:text-red-500 transition-colors">
                  Plan PRO
                </Link>
                <Link to="/login" className="block text-gray-400 hover:text-red-500 transition-colors">
                  Iniciar Sesión
                </Link>
              </div>
            </div>
          </div>
          
          <div className="border-t border-red-900/30 mt-8 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} DATAPRO Argentina. Todos los derechos reservados.
          </div>
        </div>
      </footer>

      {/* Lightbox modal — opens when any "novedades" card is clicked. The
          backdrop click closes; the inner image swallows the click so the
          user can drag-select or right-click without dismissing. */}
      {lightboxShot && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={lightboxShot.label}
          onClick={() => setLightboxShot(null)}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur flex items-center justify-center p-6 sm:p-12 cursor-zoom-out"
          data-testid="lightbox-overlay"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxShot(null); }}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 h-10 w-10 rounded-full bg-zinc-900/80 hover:bg-red-700 text-white border border-zinc-700 hover:border-red-500 transition flex items-center justify-center text-2xl leading-none cursor-pointer"
            aria-label="Cerrar"
            data-testid="lightbox-close"
          >
            ×
          </button>
          <img
            src={lightboxShot.src}
            alt={lightboxShot.alt || lightboxShot.label}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full object-contain rounded-lg shadow-2xl shadow-black cursor-default"
            data-testid="lightbox-image"
          />
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs sm:text-sm uppercase tracking-[0.22em] pointer-events-none">
            {lightboxShot.label}
          </p>
        </div>
      )}
    </div>
  );
};

export default LandingPage;

// Small reusable row that renders a label + format pills, used by the
// Replay Studio carousel slide. Kept colocated for now — only one caller.
const FormatRow = ({ icon, label, items }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-zinc-500 whitespace-nowrap">
      {icon}{label}
    </span>
    {items.map((it) => (
      <span
        key={it}
        className="inline-flex items-center rounded-md border border-zinc-700 bg-zinc-900/80 px-2 py-0.5 font-mono text-[11px] text-zinc-300"
      >
        {it}
      </span>
    ))}
  </div>
);
